/**
 * Organization Management API Service
 *
 * Handles all org management API calls: tenants, organizations,
 * users, departments, reporting structure, oversight, subscriptions.
 */
import apiClient from './client';

// ═══ Types ═══

export interface OrgThemeConfig {
  primary_color?: string | null;
  secondary_color?: string | null;
  logo_url?: string | null;
  status_overrides?: Record<string, string> | null;
}

export interface CurrentUserContext {
  id: string;
  cognito_sub: string;
  email: string;
  display_name: string | null;
  title: string | null;
  avatar_url: string | null;
  org_id: string;
  org_name: string;
  tenant_id: string;
  tenant_name: string;
  department_id: string | null;
  system_role: 'platform_admin' | 'tenant_admin' | 'org_admin' | 'user';
  status: string;
  oversight_org_ids: string[];
  enabled_modules: string[];
  user_modules: string[];
  module_roles: Record<string, string>;
  permissions: string[];
  theme_config?: OrgThemeConfig | null;
}

export interface Organization {
  id: string;
  tenant_id: string;
  name: string;
  country: string | null;
  org_type: 'parent' | 'subsidiary';
  parent_org_id: string | null;
  billing_mode: 'self' | 'sponsored';
  status: string;
  created_at: string;
  user_count?: number;
}

export interface Department {
  id: string;
  org_id: string;
  name: string;
  parent_dept_id: string | null;
  created_at: string;
}

export interface OrgUser {
  id: string;
  email: string;
  display_name: string | null;
  title: string | null;
  avatar_url: string | null;
  org_id: string;
  department_id: string | null;
  system_role: string;
  reports_to: string | null;
  status: string;
  joined_at: string | null;
  created_at: string;
  module_access: string[];
  module_roles: Record<string, string>;
}

export interface ModuleRegistryItem {
  id: string;
  name: string;
  icon: string;
  roles: { id: string; name: string }[];
  default_role: string;
}

export interface UserModuleData {
  org_enabled_modules: string[];
  user_modules: string[];
  module_roles: Record<string, string>;
}

export interface OrgTreeNode {
  id: string;
  email: string;
  display_name: string | null;
  title: string | null;
  system_role: string;
  department_id: string | null;
  reports: OrgTreeNode[];
}

export interface Subscription {
  id: string;
  org_id: string;
  tier: string;
  max_users: number | null;
  billing_email: string | null;
  sponsored_by_tenant: boolean;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
}

export interface ModuleAccess {
  id: string;
  org_id: string;
  module_id: string;
  enabled: boolean;
  activated_at: string | null;
}

// ═══ API Calls ═══

// ── Current User Context ──

export const getMyContext = async (): Promise<CurrentUserContext> => {
  const { data } = await apiClient.get('/api/org/me');
  return data;
};

export const updateMyProfile = async (profile: {
  display_name?: string;
  title?: string;
  avatar_url?: string;
}): Promise<OrgUser> => {
  const { data } = await apiClient.patch('/api/org/me', profile);
  return data;
};

// ── Organization Users ──

export const listOrgUsers = async (): Promise<OrgUser[]> => {
  const { data } = await apiClient.get('/api/org/users');
  return data;
};

export const getUser = async (userId: string): Promise<OrgUser> => {
  const { data } = await apiClient.get(`/api/org/users/${userId}`);
  return data;
};

export const inviteUser = async (invite: {
  email: string;
  display_name?: string;
  title?: string;
  system_role?: string;
  department_id?: string;
  reports_to?: string;
}): Promise<OrgUser> => {
  const { data } = await apiClient.post('/api/org/users/invite', invite);
  return data;
};

export const bulkInviteUsers = async (users: Array<{
  email: string;
  display_name?: string;
  title?: string;
  system_role?: string;
  department_id?: string;
  reports_to?: string;
}>): Promise<OrgUser[]> => {
  const { data } = await apiClient.post('/api/org/users/invite/bulk', { users });
  return data;
};

export const updateUser = async (
  userId: string,
  updates: {
    display_name?: string;
    title?: string;
    system_role?: string;
    department_id?: string;
    reports_to?: string;
    status?: string;
  }
): Promise<OrgUser> => {
  const { data } = await apiClient.patch(`/api/org/users/${userId}`, updates);
  return data;
};

export const deactivateUser = async (userId: string): Promise<void> => {
  await apiClient.delete(`/api/org/users/${userId}`);
};

// ── Departments ──

export const listDepartments = async (): Promise<Department[]> => {
  const { data } = await apiClient.get('/api/org/departments');
  return data;
};

export const createDepartment = async (dept: {
  name: string;
  parent_dept_id?: string;
}): Promise<Department> => {
  const { data } = await apiClient.post('/api/org/departments', dept);
  return data;
};

export const updateDepartment = async (
  deptId: string,
  updates: { name?: string; parent_dept_id?: string }
): Promise<Department> => {
  const { data } = await apiClient.patch(`/api/org/departments/${deptId}`, updates);
  return data;
};

export const deleteDepartment = async (deptId: string): Promise<void> => {
  await apiClient.delete(`/api/org/departments/${deptId}`);
};

// ── Reporting Structure ──

export const setReporting = async (
  userId: string,
  managerId: string | null
): Promise<OrgUser> => {
  const { data } = await apiClient.post('/api/org/reporting', {
    user_id: userId,
    manager_id: managerId,
  });
  return data;
};

export const bulkUpdateReporting = async (
  updates: Array<{ user_id: string; manager_id: string | null }>
): Promise<OrgUser[]> => {
  const { data } = await apiClient.post('/api/org/reporting/bulk', { updates });
  return data;
};

export const getOrgTree = async (): Promise<OrgTreeNode[]> => {
  const { data } = await apiClient.get('/api/org/structure');
  return data;
};

export const getMyTeam = async (): Promise<OrgUser[]> => {
  const { data } = await apiClient.get('/api/org/my-team');
  return data;
};

// ── Tenant-Level (Tenant Admin) ──

export const listOrganizations = async (): Promise<Organization[]> => {
  const { data } = await apiClient.get('/api/org/tenant/organizations');
  return data;
};

export const createOrganization = async (org: {
  name: string;
  country?: string;
  org_type?: string;
  parent_org_id?: string;
  billing_mode?: string;
}): Promise<Organization> => {
  const { data } = await apiClient.post('/api/org/tenant/organizations', org);
  return data;
};

export const listTenantUsers = async (): Promise<OrgUser[]> => {
  const { data } = await apiClient.get('/api/org/tenant/users');
  return data;
};

// ── Subscription & Modules ──

export const getSubscription = async (): Promise<Subscription> => {
  const { data } = await apiClient.get('/api/org/subscription');
  return data;
};

export const listModules = async (): Promise<ModuleAccess[]> => {
  const { data } = await apiClient.get('/api/org/modules');
  return data;
};

// ── User Module Management ──

export const getUserModules = async (userId: string): Promise<UserModuleData> => {
  const { data } = await apiClient.get(`/api/org/users/${userId}/modules`);
  return data;
};

export const setUserModules = async (
  userId: string,
  modules: { module_id: string; enabled: boolean }[]
): Promise<void> => {
  await apiClient.put(`/api/org/users/${userId}/modules`, { modules });
};

export const setUserModuleRole = async (
  userId: string,
  moduleId: string,
  role: string
): Promise<void> => {
  await apiClient.put(`/api/org/users/${userId}/modules/${moduleId}/role`, { role });
};

export const getModuleRegistry = async (): Promise<ModuleRegistryItem[]> => {
  const { data } = await apiClient.get('/api/org/module-registry');
  return data;
};

// ── User Admin Operations ──

export const adminSetPassword = async (userId: string, password: string): Promise<void> => {
  await apiClient.post(`/api/org/users/${userId}/set-password`, { password });
};

export const requestPasswordReset = async (userId: string): Promise<void> => {
  await apiClient.post(`/api/org/users/${userId}/password-reset`);
};

export const impersonateUser = async (userId: string): Promise<{
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
}> => {
  const { data } = await apiClient.post(`/api/org/users/${userId}/impersonate`);
  return data;
};

// ── Sales Public API Key ──

export const getSalesPublicApiKey = async (orgId?: string): Promise<{ public_api_key: string }> => {
  const params = orgId ? { org_id: orgId } : {};
  const { data } = await apiClient.get('/api/org/organization/sales-public-api-key', { params });
  return data;
};

export const regenerateSalesPublicApiKey = async (orgId?: string): Promise<{ public_api_key: string }> => {
  const params = orgId ? { org_id: orgId } : {};
  const { data } = await apiClient.post('/api/org/organization/sales-public-api-key/regenerate', {}, { params });
  return data;
};

// ── Organization LLM API Key ──

export interface LlmApiKeyStatus {
  configured: boolean;
  hint: string | null;
  using_platform_default: boolean;
}

export const getOrgLlmApiKey = async (orgId?: string): Promise<LlmApiKeyStatus> => {
  const params = orgId ? { org_id: orgId } : {};
  const { data } = await apiClient.get('/api/org/organization/llm-api-key', { params });
  return data;
};

export const setOrgLlmApiKey = async (apiKey: string, orgId?: string): Promise<LlmApiKeyStatus> => {
  const params = orgId ? { org_id: orgId } : {};
  const { data } = await apiClient.put('/api/org/organization/llm-api-key', { api_key: apiKey }, { params });
  return data;
};

export const clearOrgLlmApiKey = async (orgId?: string): Promise<LlmApiKeyStatus> => {
  const params = orgId ? { org_id: orgId } : {};
  const { data } = await apiClient.delete('/api/org/organization/llm-api-key', { params });
  return data;
};

// ── Theme ──

export const getOrgTheme = async (): Promise<OrgThemeConfig> => {
  const { data } = await apiClient.get('/api/org/theme');
  return data;
};

export const updateOrgTheme = async (theme: OrgThemeConfig): Promise<OrgThemeConfig> => {
  const { data } = await apiClient.put('/api/org/theme', theme);
  return data;
};
